import { defineMessages } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

export enum TestId {
	DIALOG = 'analytics-dialog',
	// CAUTION: if you update this you'll need to update the contents of the scripts in src/ssr-scripts.
	DIALOG_SKELETON = 'analytics-dialog-skeleton',
	LINK = 'analytics-link',
	LINK_SKELETON = 'analytics-link-skeleton',
	CLOSE_BUTTON = 'dialog-close-button',
	PILLS = 'analytics-pills',
	INFO_PANEL_1 = 'analytics-info-panel-1',
	INFO_PANEL_2 = 'analytics-info-panel-2',
	CHART = 'analytics-chart',
	USER_LIST = 'analytics-user-list',
	SUMMARIZE_CHANGES = 'analytics-summarize-changes',
}

export const i18n: {
	dialogContainerAriaLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	dialogLoadingText: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	dialogLoadingTextMentioned: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	dialogMentionedUsersViewed: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	dialogMentionedPeopleAndAgentsViewed: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	dialogMentionedUsersAllSeen: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	dialogProgressbarCountAriaLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	dialogProgressbarCountAriaLabelPeopleAndAgents: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	dialogProgressbarAriaLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	dialogProgressbarAriaLabelPeopleAndAgents: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	graphGranularityWeek: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	graphCreatedAtDateTimeLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	graphDateToDateTimeLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	lastViewedJustNow: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	lastViewedMinutesAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	lastViewedHoursAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	lastViewedDaysAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	lastViewedWeeksAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	lastViewedMonthsAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	lastViewedYearsAgo: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	errorMessage: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	errorSuggestion: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	viewedMentionsLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	notViewedMentionsLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	mentionedPeopleLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	linksTabTopInboundColumnHeaderLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	linksTabTopOutboundColumnHeaderLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	linksTabClicksColumnHeaderLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	linksTabViewAllLinkLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	linksTabViewMoreButtonLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	linksTabNoInboundLinksMessage: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	linksTabNoOutboundLinksMessage: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	linksTabExternalLinkContentItem: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	linksTabExternalLinkContentItemInfoTooltip: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	linksTabExternalLinkContentItemInfoTooltipLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	linksTabErrorContentItem: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	dialogContainerAriaLabel: {
		id: 'confluence-analytics.dialog.container-aria-label',
		defaultMessage: 'Confluence Analytics',
		description: 'Label announced by the screen reader when the analytics dialog opens.',
	},
	dialogLoadingText: {
		id: 'confluence-analytics.dialog.loading',
		defaultMessage: 'Loading data...',
		description: 'Text to display while the dialog is loading.',
	},
	dialogLoadingTextMentioned: {
		id: 'confluence-analytics.dialog.loading.mentioned',
		defaultMessage: 'Loading mentions data...',
		description: 'Text to display while the dialog for mentioned users data is loading.',
	},
	dialogMentionedUsersViewed: {
		id: 'confluence-analytics.dialog.mentioned-users-viewed',
		defaultMessage:
			'mentioned {seenMentionedUsersCount, plural, one {user} other {users}} viewed this page',
		description: 'Text following the fraction of how many mentioned users viewed this page.',
	},
	dialogMentionedPeopleAndAgentsViewed: {
		id: 'confluence-analytics.dialog.mentioned-people-and-agents-viewed',
		defaultMessage: 'mentioned people and agents viewed this page',
		description:
			'Text following the fraction of how many mentioned people and agents viewed this page when agent analytics is enabled.',
	},
	dialogMentionedUsersAllSeen: {
		id: 'confluence-analytics.dialog.mentioned-users-all-seen',
		defaultMessage: 'Everyone mentioned has viewed',
		description: 'Text to indicate all mentioned users have viewed a page.',
	},
	dialogProgressbarCountAriaLabel: {
		id: 'confluence-analytics.dialog.progress-bar.count-aria-label',
		defaultMessage:
			'{seenMentionedUsersCount} of {totalMentionedUsersCount} mentioned {seenMentionedUsersCount, plural, one {user} other {users}} viewed this page',
		description:
			'Label announced by the screen reader to specify the fraction of how many of the total mentioned users have seen the page.',
	},
	dialogProgressbarCountAriaLabelPeopleAndAgents: {
		id: 'confluence-analytics.dialog.progress-bar.count-aria-label.people-and-agents',
		defaultMessage:
			'{seenMentionedUsersCount} of {totalMentionedUsersCount} mentioned people and agents viewed this page',
		description:
			'Label announced by the screen reader for how many mentioned people and agents have seen the page when agent analytics is enabled.',
	},
	dialogProgressbarAriaLabel: {
		id: 'confluence-analytics.dialog.progress-bar-aria-label',
		defaultMessage: 'Mentioned users progress bar',
		description: 'Label announced by the screen reader to describe the progress bar.',
	},
	dialogProgressbarAriaLabelPeopleAndAgents: {
		id: 'confluence-analytics.dialog.progress-bar-aria-label.people-and-agents',
		defaultMessage: 'Mentioned people and agents progress bar',
		description:
			'Label announced by the screen reader for the Mentions progress bar when agent analytics is enabled.',
	},
	graphGranularityWeek: {
		id: 'confluence-analytics.graph.granularity.week',
		defaultMessage: 'Week of {date}',
		description:
			'Sub-text for analytics graph which describes the granularity of data being viewed.',
	},
	graphCreatedAtDateTimeLabel: {
		id: 'confluence-analytics.graph.label.created-at',
		defaultMessage: 'Created {date} at {time}',
		description: 'Label specifying the date and time at which content was created.',
	},
	graphDateToDateTimeLabel: {
		id: 'confluence-analytics.graph.label.date-to-datetime',
		defaultMessage: '{fromDate} to {toDate} ({toTime})',
		description: 'Label specifying a range of time for the graph being displayed.',
	},
	lastViewedJustNow: {
		id: 'confluence-analytics.dialog.viewers-list.viewed-just-now',
		defaultMessage: 'Just now',
		description: 'Label specifying a viewer saw the page just less than a minute ago.',
	},
	lastViewedMinutesAgo: {
		id: 'confluence-analytics.dialog.viewers-list.viewed-minutes-ago',
		defaultMessage: '{minuteCount} {minuteCount, plural, one {minute} other {minutes}} ago',
		description: 'Label specifying a viewer saw the page minutes ago, should always be plural.',
	},
	lastViewedHoursAgo: {
		id: 'confluence-analytics.dialog.viewers-list.viewed-hours-ago',
		defaultMessage: '{hourCount} {hourCount, plural, one {hour} other {hours}} ago',
		description: 'Label specifying a viewer saw the page hours ago, should always be plural.',
	},
	lastViewedDaysAgo: {
		id: 'confluence-analytics.dialog.viewers-list.viewed-days-ago',
		defaultMessage: '{dayCount} {dayCount, plural, one {day} other {days}} ago',
		description: 'Label specifying a viewer saw the page days ago, should always be plural.',
	},
	lastViewedWeeksAgo: {
		id: 'confluence-analytics.dialog.viewers-list.viewed-weeks-ago',
		defaultMessage: '{weekCount} {weekCount, plural, one {week} other {weeks}} ago',
		description: 'Label specifying a viewer saw the page weeks ago, should always be plural.',
	},
	lastViewedMonthsAgo: {
		id: 'confluence-analytics.dialog.viewers-list.viewed-months-ago',
		defaultMessage: '{monthCount} {monthCount, plural, one {month} other {months}} ago',
		description: 'Label specifying a viewer saw the page months ago, should always be plural.',
	},
	lastViewedYearsAgo: {
		id: 'confluence-analytics.dialog.viewers-list.viewed-years-ago',
		defaultMessage: '{yearCount} {yearCount, plural, one {year} other {years}} ago',
		description: 'Label specifying a viewer saw the page years ago, should always be plural.',
	},
	errorMessage: {
		id: 'confluence-analytics.dialog.loading.error-message',
		defaultMessage: 'We’re having trouble loading analytics.',
		description: 'Error message to display when the dialog fails to load.',
	},
	errorSuggestion: {
		id: 'confluence-analytics.dialog.loading.error-suggestion',
		defaultMessage: 'Refresh to try again.',
		description: 'Suggested action to take when the dialog failed to load.',
	},
	viewedMentionsLabel: {
		id: 'confluence-analytics.dialog.mentions-list.viewed.label',
		defaultMessage: 'Viewed',
		description: 'Label for the list of mentioned users on a page that have since viewed the page.',
	},
	notViewedMentionsLabel: {
		id: 'confluence-analytics.dialog.mentions-list.not-viewed.label',
		defaultMessage: 'Not viewed',
		description:
			'Label for the list of mentioned users on a page that have not yet viewed the page.',
	},
	mentionedPeopleLabel: {
		id: 'confluence-analytics.dialog.mentions-list.mentioned-people.label',
		defaultMessage: 'Mentioned people',
		description:
			'Label for the list of mentioned users on a page that have viewed or not yet viewed the page.',
	},
	linksTabTopInboundColumnHeaderLabel: {
		id: 'confluence-analytics.dialog.links-tab.inbound.column-header',
		defaultMessage: 'Top Inbound Links',
		description: 'Label for the list of top links that are inbound to the current content.',
	},
	linksTabTopOutboundColumnHeaderLabel: {
		id: 'confluence-analytics.dialog.links-tab.outbound.column-header',
		defaultMessage: 'Top Outbound Links',
		description: 'Label for the list of top links that are outbound from the current content.',
	},
	linksTabClicksColumnHeaderLabel: {
		id: 'confluence-analytics.dialog.links-tab.clicks.column-header',
		defaultMessage: 'Clicks',
		description: 'Column label for the number of clicks on a link in the links tab.',
	},
	linksTabViewAllLinkLabel: {
		id: 'confluence-analytics.dialog.links-tab.view-all-link',
		defaultMessage: 'View all',
		description: 'Label for a link to view all links in the links tab.',
	},
	linksTabViewMoreButtonLabel: {
		id: 'confluence-analytics.dialog.links-tab.view-more-button',
		defaultMessage: 'View more insights',
		description: 'Label for a button to view more links in the links tab.',
	},
	linksTabNoInboundLinksMessage: {
		id: 'confluence-analytics.dialog.links-tab.no-inbound-links',
		defaultMessage:
			'No other pages reference this page. As this page draws traffic from other sources, those links will appear here.',
		description: 'Message to display when there are no inbound links to display in the links tab.',
	},
	linksTabNoOutboundLinksMessage: {
		id: 'confluence-analytics.dialog.links-tab.no-outbound-links',
		defaultMessage:
			'There are no outbound links on this page. When you add one, you can track its click count here.',
		description: 'Message to display when there are no outbound links to display in the links tab.',
	},
	linksTabExternalLinkContentItem: {
		id: 'confluence-analytics.dialog.links-tab.external-link-content-item',
		defaultMessage: 'External links',
		description: 'Label for the external links row in the analytics dialog.',
	},
	linksTabExternalLinkContentItemInfoTooltip: {
		id: 'confluence-analytics.dialog.links-tab.external-link-content-item.info.tooltip',
		defaultMessage: 'Only aggregated data is available for external site links.',
		description: 'Tooltip for the external link icon in the analytics dialog',
	},
	linksTabExternalLinkContentItemInfoTooltipLabel: {
		id: 'confluence-analytics.dialog.links-tab.external-link-content-item.info.tooltip-label',
		defaultMessage: 'External links tooltip button',
		description: 'Accessible label for external links tooltip',
	},
	linksTabErrorContentItem: {
		id: 'confluence-analytics.dialog.links-tab.error-content-item',
		defaultMessage: 'An error occurred while loading this link. Refresh the page to try again.',
		description:
			'Row title for when an error occurred when loading link data in the analytics dialog.',
	},
});

export enum DialogPillName {
	VIEWS = 'views',
	MENTIONS = 'mentions',
	LINKS = 'links',
}

export type DialogPillToPanelMapping = Record<DialogPillName, React.ReactNode>;

export type Pill<TPillName extends string = string> = {
	content: React.ReactNode;
	name: TPillName;
};

export const pillToLoadingTextI18n: Record<DialogPillName, MessageDescriptor> = {
	[DialogPillName.VIEWS]: i18n.dialogLoadingText,
	[DialogPillName.MENTIONS]: i18n.dialogLoadingTextMentioned,
	[DialogPillName.LINKS]: i18n.dialogLoadingText,
};

export const dialogToPageAnalyticsTab: Record<DialogPillName, 'Overview' | 'Mentions' | 'Links'> = {
	[DialogPillName.VIEWS]: 'Overview',
	[DialogPillName.MENTIONS]: 'Mentions',
	[DialogPillName.LINKS]: 'Links',
};
